import { BadgeEuro, Eye, MousePointer2, Presentation, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import UsersGroup from '@/components/icons/UsersGroup';
import { resolveNotStartedKpiValue } from '@/lib/analyticsSessionStatus';
import type { SessionSummary, WebinarSessionStatus } from '../types';

export const SessionKpiGrid = ({
  summary,
  status,
}: {
  summary: SessionSummary;
  status?: WebinarSessionStatus | null;
}) => {
  const { t } = useTranslation('analytics');
  const participationPct =
    summary.registrantsCount > 0
      ? Math.round((summary.participantsCount / summary.registrantsCount) * 100)
      : 0;

  const metrics = [
    {
      label: t('session.kpi.participants.label'),
      value: `${participationPct}%`,
      sub: t('session.kpi.participants.sub', {
        count: summary.participantsCount,
        total: summary.registrantsCount,
      }),
      Icon: UsersGroup,
      color: 'text-[#0C0E21]',
      bg: 'bg-decorative-2',
    },
    {
      label: t('session.kpi.activeParticipation.label'),
      // Sans date de démarrage le back remonte 100 % : ne rien afficher plutôt qu'un engagement fictif.
      value: resolveNotStartedKpiValue(`${summary.activeParticipationRate}%`, status),
      sub: t('session.kpi.activeParticipation.sub'),
      Icon: Zap,
      color: 'text-[#f59e0b]',
      bg: 'bg-[#f59e0b20]',
    },
    {
      label: t('session.kpi.fullView.label'),
      value: resolveNotStartedKpiValue(`${summary.fullViewRate}%`, status),
      sub: null,
      Icon: Presentation,
      color: 'text-[#0C0E21]',
      bg: 'bg-decorative-2',
    },
    {
      label: t('session.kpi.averageWatchTime.label'),
      value: resolveNotStartedKpiValue(summary.averageWatchTime, status),
      sub: null,
      Icon: Eye,
      color: 'text-[#0C0E21]',
      bg: 'bg-decorative-2',
    },
    {
      label: t('session.kpi.offerClicks.label'),
      value: summary.offerClicksCount,
      sub: t('session.kpi.offerClicks.sub'),
      Icon: MousePointer2,
      color: 'text-[#FF6526]',
      bg: 'bg-[#FF652620]',
    },
    {
      label: t('session.kpi.conversions.label'),
      value: summary.salesCount,
      sub: t('session.kpi.conversions.sub', { amount: summary.totalRevenueAmount }),
      Icon: BadgeEuro,
      color: 'text-[#2EB87A]',
      bg: 'bg-[#2EB87A22]',
    },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {metrics.map((m, i) => (
        <div
          key={i}
          className="bg-white border border-medium rounded-[20px] p-4 shadow-sm flex justify-between items-start"
        >
          <div className="flex flex-col gap-1">
            <span className="text-xs text-[#6D6E7A] font-normal">{m.label}</span>
            <div className="flex items-baseline flex-col gap-1.5">
              <span className="text-xl font-bold text-dark">{m.value}</span>
              {m.sub && <span className="text-[11px] text-[#6D6E7A] font-normal">{m.sub}</span>}
            </div>
          </div>
          <div className={`p-2.5 ${m.bg} rounded-xl`}>
            <m.Icon className={`w-6 h-6 shrink-0 ${m.color}`} />
          </div>
        </div>
      ))}
    </div>
  );
};
